import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Avatar } from '../Profile/Avatar';
import { Username } from '../Profile/Username';
import { formatCategoryName, formatWorkArrangement } from '../../lib/utils/formatters';
import { VerificationStatusBadge } from '../Practitioners/VerificationStatus';
import { wellnessConcerns } from '../../lib/constants/wellnessConcerns';
import type { Practitioner } from '../../types/practitioners';

interface PractitionerCardProps {
  practitioner: Practitioner;
  showStatus?: boolean;
  distance?: number;
  isInactive?: boolean;
}

// Helper function to convert currency code to symbol
function getCurrencySymbol(currency: string = 'EUR'): string {
  const symbols: Record<string, string> = {
    'EUR': '€',
    'USD': '$',
    'GBP': '£',
    'CHF': 'CHF'
  };
  
  return symbols[currency] || currency;
}

export function PractitionerCard({ practitioner, showStatus = false, distance, isInactive = false }: PractitionerCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayImage = practitioner.images?.[0] || 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&q=80&w=400';

  const price = practitioner.starting_price ? `${getCurrencySymbol(practitioner.currency)}${practitioner.starting_price}` : null;

  const getHelpsWithTags = (helpsWithString: string) => {
    if (!helpsWithString) return [];
    return helpsWithString.split(',')
      .slice(0, 3) // Show max 3 tags
      .map(tag => {
        const concern = wellnessConcerns.find(c => c.value === tag.trim());
        return concern ? concern.label : tag.trim();
      });
  };

  const helpsWithTags = getHelpsWithTags(practitioner.helps_with || '');

  const handleCardClick = () => {
    navigate(`/practitioners/${practitioner.slug}`);
  };

  return (
    <div
      onClick={handleCardClick}
      className="transition-all duration-150 cursor-pointer active:scale-[0.98]"
    >
      <div className="aspect-w-16 aspect-h-9 relative rounded-2xl overflow-hidden">
        <img
          src={displayImage}
          alt={practitioner.title}
          className="w-full h-48 object-cover"
          loading="lazy"
        />
      </div>
      <div className="pt-4">
        <div className="flex items-center justify-between mb-4">
          {practitioner.user && (
            <Link 
              to={`/profile/${practitioner.user.username}/listings`}
              className="flex items-center space-x-2 group flex-1 min-w-0"
              onClick={(e) => e.stopPropagation()}
            >
              <Avatar 
                url={practitioner.user.avatar_url} 
                size="sm" 
                userId={practitioner.user_id}
                editable={false}
              />
              <div className="min-w-0">
                <Username 
                  username={practitioner.user.username || 'Anonymous'}
                  userId={practitioner.user_id}
                  verified={!!practitioner.user?.verified}
                  className="text-sm text-content group-hover:text-accent-text truncate"
                />
                <p className="text-xs text-content/60 mt-0.5">Practitioner</p>
              </div>
            </Link>
          )}
          <div className="flex items-center space-x-2 flex-wrap gap-1">
            <span className="px-2 py-1 text-xs font-medium bg-accent-base text-accent-text rounded-full">
              {formatCategoryName(practitioner.category)}
            </span>
            {isInactive && (
              <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                Inactive
              </span>
            )}
            {showStatus && practitioner.approval_status === 'pending' && (
              <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                {t('common.pending')}
              </span>
            )}
            {showStatus && (
              <>
                {practitioner.id_verification_status && (
                  <VerificationStatusBadge
                    type="id"
                    status={practitioner.id_verification_status}
                    className="scale-75 origin-right"
                  />
                )}
                {practitioner.certificate_verification_status && (
                  <VerificationStatusBadge
                    type="certificate"
                    status={practitioner.certificate_verification_status}
                    className="scale-75 origin-right"
                  />
                )}
              </>
            )}
          </div>
        </div>
        
        <h3 className="text-lg font-semibold text-content mb-2">
          {practitioner.title}
        </h3>

        <div className="space-y-2 mb-4">
          <div className="flex items-center text-sm text-content/80">
            <img src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/location-icon.png" alt="Location" className="h-4 w-4 mr-2" />
            <span>{practitioner.address || practitioner.country}</span>
            {distance !== undefined && (
              <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                {distance < 1 ? `${Math.round(distance * 1000)}m` : `${distance.toFixed(1)}km`}
              </span>
            )}
          </div>
          <div className="flex items-center text-sm text-content/80">
            <img src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/globe-icon.png" alt="Work" className="h-4 w-4 mr-2" />
            <span>{formatWorkArrangement(practitioner.work_arrangement)}</span>
          </div>
          {price && (
            <div className="flex items-center text-sm text-content/80 mt-2">
              <img src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/coins-icon.png" alt="Price" className="h-4 w-4 mr-2" />
              <span>{t('common.from')} {price}</span>
            </div>
          )}
          {helpsWithTags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {helpsWithTags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-accent-text/10 text-accent-text"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
